/**
 * Catalog Toolbar
 * – SHOP BY panel toggle
 * – SHOP BY card click → clear all filters, apply one, AJAX render
 * – Client-side product search
 * – State preservation across facets.js AJAX re-renders
 * – Filter dropdown toggle text sync after render
 */

(function () {
  'use strict';

  var _suppressPanelRestore = false;

  /* ── Enable exactly one copy of each filter/sort field ──────
       The SHOP BY panel duplicates every filter field and the sort
       select inside the same #FacetFiltersForm. display:none does
       not exclude fields from FormData, so whichever copy is hidden
       must be `disabled` or facets.js re-serializes stale values
       (active filters can never be unchecked, sort_by is sent
       twice). Inputs marked data-facet-disabled (count 0) always
       stay disabled.                                              */
  function syncShopByFieldState() {
    document.querySelectorAll('.catalog-toolbar__form').forEach(function (form) {
      var panelOpen = form.hasAttribute('data-shopby-open');

      function setDisabled(scope, disabled) {
        form.querySelectorAll(scope + ' input, ' + scope + ' select').forEach(function (field) {
          field.disabled = disabled || field.hasAttribute('data-facet-disabled');
        });
      }

      setDisabled('.catalog-toolbar__shopby', !panelOpen);
      setDisabled('.catalog-toolbar__filters', panelOpen);
      setDisabled('.catalog-toolbar__header-controls', panelOpen);
    });
  }

  /* ── SHOP BY toggle ────────────────────────────────────────── */
  function setShopByPanel(btn, open) {
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;

    btn.setAttribute('aria-expanded', String(open));
    panel.style.display = open ? '' : 'none';

    var form = btn.closest('.catalog-toolbar__form');
    if (form) {
      if (open) form.setAttribute('data-shopby-open', '');
      else form.removeAttribute('data-shopby-open');
    }
    syncShopByFieldState();
  }

  function initShopByToggle() {
    document.querySelectorAll('.catalog-toolbar__shopby-btn').forEach(function (btn) {
      if (btn._catalogBound) return;
      btn._catalogBound = true;
      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        setShopByPanel(btn, !isOpen);
      });

      // Hover is deliberately limited to precise-pointer devices. The form
      // contains both trigger and panel, so it remains open while moving
      // between them and closes only after leaving the whole toolbar.
      var form = btn.closest('.catalog-toolbar__form');
      if (!form || form._catalogShopByHoverBound) return;
      form._catalogShopByHoverBound = true;
      btn.addEventListener('mouseenter', function () {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          setShopByPanel(btn, true);
        }
      });
      form.addEventListener('mouseleave', function () {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          setShopByPanel(btn, false);
        }
      });
    });
  }

  /* ── Close the SHOP BY panel (shared helper) ───────────────── */
  function closeShopByPanel() {
    var btn = document.querySelector('.catalog-toolbar__shopby-btn');
    if (btn) {
      setShopByPanel(btn, false);
      return;
    }
    syncShopByFieldState();
  }

  /* ── Filter dropdowns ────────────────────────────────────────
     The former full-page pseudo-element used to close a dropdown
     intercepted the first click on another dropdown. Close siblings
     and click-away targets in JavaScript instead, so one click opens
     the next filter. */
  function initFilterDropdowns() {
    if (document._catalogDropdownsBound) return;
    document._catalogDropdownsBound = true;
    document.addEventListener('click', function (event) {
      var clickedDropdown = event.target.closest('.catalog-toolbar__dropdown');
      document.querySelectorAll('.catalog-toolbar__dropdown[open]').forEach(function (dropdown) {
        if (dropdown !== clickedDropdown) dropdown.removeAttribute('open');
      });
    });
  }

  /* ── SHOP BY card click ────────────────────────────────────── *
   *  On the catch-all collection, clear every filter and apply just the
   *  selected value through the facets renderer. A card clicked elsewhere
   *  still navigates to /collections/all, preserving its intended route. */
  function initShopByCards() {
    document.querySelectorAll('.catalog-toolbar__card[data-param]').forEach(function (card) {
      if (card._catalogBound) return;
      card._catalogBound = true;
      card.addEventListener('click', function (e) {
        e.preventDefault();
        var param = card.dataset.param;
        var value = card.dataset.value;
        var targetUrl = '/collections/all?' + encodeURIComponent(param) + '=' + encodeURIComponent(value);

        if (
          window.location.pathname === '/collections/all' &&
          typeof FacetFiltersForm !== 'undefined' &&
          document.querySelector('#FacetFiltersForm')
        ) {
          e.preventDefault();
          var params = new URLSearchParams(new FormData(document.querySelector('#FacetFiltersForm')));
          Array.from(params.keys()).forEach(function (key) {
            if (key.indexOf('filter.') === 0 || key === 'sort_by') params.delete(key);
          });
          params.append(param, value);

          _suppressPanelRestore = true;
          closeShopByPanel();
          FacetFiltersForm.renderPage(params.toString(), null);
          return;
        }

        closeShopByPanel();
        window.location.href = targetUrl;
      });
    });
  }

  /* ── Catalog-wide product search ────────────────────────────
       The toolbar is rendered on a single paginated collection page.
       Filtering its existing cards makes valid products on later pages
       disappear, so send the query to Shopify's product search instead. */
  function initProductSearch() {
    document.querySelectorAll('.catalog-toolbar__input').forEach(function (input) {
      if (input._catalogBound) return;
      input._catalogBound = true;
      var timer;
      input.addEventListener('input', function (e) {
        // Prevent facets.js from treating keystrokes as filter changes.
        e.stopPropagation();
        clearTimeout(timer);
        timer = setTimeout(function () {
          var q = input.value.trim();

          // Sync every search input so both copies stay in agreement.
          document.querySelectorAll('.catalog-toolbar__input').forEach(function (other) {
            if (other !== input) other.value = input.value;
          });

          if (!q) return;

          var searchUrl = new URL('/search', window.location.origin);
          searchUrl.searchParams.set('q', q);
          searchUrl.searchParams.set('options[prefix]', 'last');
          searchUrl.searchParams.set('type', 'product');

          if (window.location.pathname + window.location.search !== searchUrl.pathname + searchUrl.search) {
            window.location.assign(searchUrl.pathname + searchUrl.search);
          }
        }, 350);
      });
    });
  }

  /* ── Sync collection hero with server-rendered facet state ──
       A facets render returns authoritative active values, including their
       server ordering and zero-result state. The collection banner itself
       may sit outside the replaced grid, so copy that state from the
       response rather than trying to reconstruct it from the raw URL. */
  function facetHeroData(html) {
    var scope = html ? new DOMParser().parseFromString(html, 'text/html') : document;
    var el = scope.querySelector('[data-facet-hero-data]');
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  }

  function syncCollectionHero(html) {
    var data = facetHeroData(html);
    var titleEl = document.querySelector('[data-hero-title]');
    if (!data || !titleEl) return;

    var title = data.title;
    if (titleEl.textContent !== title) titleEl.textContent = title;

    var facetDescEl = document.querySelector('[data-hero-facet-desc]');
    var defaultDescEl = document.querySelector('[data-hero-default-desc]');
    var facetActive = Boolean(data.isFacetHero);
    if (facetDescEl) {
      if (facetActive && data.description) {
        facetDescEl.innerHTML = data.description;
        facetDescEl.removeAttribute('hidden');
      } else {
        facetDescEl.setAttribute('hidden', '');
      }
    }
    if (defaultDescEl) {
      if (facetActive) defaultDescEl.setAttribute('hidden', '');
      else defaultDescEl.removeAttribute('hidden');
    }
    if (data.documentTitle) document.title = data.documentTitle;
  }

  /* ── Sync dropdown toggle text from server HTML ─────────────
       facets.js skips full innerHTML replacement for the filter
       the user just interacted with (countsToRender path), but its
       renderCounts targets .facets__summary which doesn't exist in
       our toolbar. This function patches every toggle's display
       text to match what the server returned.                     */
  function syncDropdownToggles(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var serverFilters = parsed.querySelectorAll('#FacetFiltersForm .js-filter');

    serverFilters.forEach(function (serverEl) {
      var domEl = document.getElementById(serverEl.id);
      if (!domEl) return;
      var serverText = serverEl.querySelector('.catalog-toolbar__dropdown-text');
      var domText = domEl.querySelector('.catalog-toolbar__dropdown-text');
      if (serverText && domText && domText.textContent !== serverText.textContent) {
        domText.textContent = serverText.textContent;
      }
    });
  }

  /* ── Preserve toolbar state across AJAX re-renders ───────── */
  function preserveState() {
    if (typeof FacetFiltersForm === 'undefined') return;

    var origRenderSection = FacetFiltersForm.renderSection;
    FacetFiltersForm.renderSection = function (html, event, updateEvent) {
      var btn = document.querySelector('.catalog-toolbar__shopby-btn');
      var wasOpen = !_suppressPanelRestore && btn && btn.getAttribute('aria-expanded') === 'true';

      var searchVals = [];
      document.querySelectorAll('.catalog-toolbar__input').forEach(function (inp) {
        searchVals.push(inp.value);
      });

      origRenderSection.call(this, html, event, updateEvent);

      syncDropdownToggles(html);

      requestAnimationFrame(function () {
        init();

        if (wasOpen) {
          var newBtn = document.querySelector('.catalog-toolbar__shopby-btn');
          var newPanel = document.getElementById('ShopByPanel');
          if (newBtn) newBtn.setAttribute('aria-expanded', 'true');
          if (newPanel) newPanel.style.display = '';
          var newForm = document.querySelector('.catalog-toolbar__form');
          if (newForm) newForm.setAttribute('data-shopby-open', '');
        }
        // renderFilters re-inserted server HTML (panel copies disabled,
        // row copies enabled) — reconcile with the actual panel state.
        syncShopByFieldState();

        var inputs = document.querySelectorAll('.catalog-toolbar__input');
        var restoredQuery = '';
        inputs.forEach(function (inp, i) {
          if (searchVals[i]) {
            inp.value = searchVals[i];
            restoredQuery = searchVals[i];
          }
        });

        // Re-apply client-side search after AJAX replaced the grid.
        if (restoredQuery) {
          var q = restoredQuery.trim().toLowerCase();
          document.querySelectorAll('#product-grid .grid__item').forEach(function (card) {
            card.style.display = card.textContent.toLowerCase().includes(q) ? '' : 'none';
          });
        }

        toggleActivePills();
        syncCollectionHero(html);
        if (_dismissMobileFacetsAfterRender) {
          _dismissMobileFacetsAfterRender = false;
          dismissMobileFacets();
        } else {
          clearOrphanedMobileFacetsLock();
        }
        _suppressPanelRestore = false;
      });
    };
  }

  /* ── Show/hide active-filter pills based on content ───────── */
  function toggleActivePills() {
    document.querySelectorAll('.catalog-toolbar__active-filters').forEach(function (el) {
      var hasPills = el.querySelector('.catalog-toolbar__pill') !== null;
      if (hasPills) {
        el.removeAttribute('hidden');
        el.removeAttribute('aria-hidden');
      } else {
        el.setAttribute('hidden', '');
        el.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /* ── Hide sticky Filter & sort when footer is in view ─────── */
  var _footerHideObserver = null;

  function syncMobileFilterFooterVisibility(footerVisible) {
    document.querySelectorAll('.catalog-toolbar-wrap .mobile-facets__wrapper').forEach(function (bar) {
      if (bar.querySelector('details[open]')) {
        bar.classList.remove('is-footer-hidden');
        return;
      }
      bar.classList.toggle('is-footer-hidden', !!footerVisible);
    });
  }

  function initMobileFilterFooterHide() {
    if (_footerHideObserver || !window.IntersectionObserver) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    var footer = document.querySelector('footer.footer');
    if (!footer) return;

    _footerHideObserver = new IntersectionObserver(
      function (entries) {
        var visible = entries.some(function (entry) {
          return entry.isIntersecting;
        });
        syncMobileFilterFooterVisibility(visible);
      },
      { root: null, threshold: 0, rootMargin: '0px' }
    );
    _footerHideObserver.observe(footer);

    document.addEventListener(
      'toggle',
      function (event) {
        var details = event.target;
        if (!(details instanceof HTMLDetailsElement)) return;
        if (!details.closest('.catalog-toolbar-wrap .mobile-facets__wrapper')) return;
        if (details.open) {
          details.closest('.mobile-facets__wrapper').classList.remove('is-footer-hidden');
        } else if (_footerHideObserver) {
          // Re-sync from current footer intersection via a cheap rect check.
          var footerEl = document.querySelector('footer.footer');
          if (!footerEl) return;
          var rect = footerEl.getBoundingClientRect();
          var visible = rect.top < window.innerHeight && rect.bottom > 0;
          syncMobileFilterFooterVisibility(visible);
        }
      },
      true
    );
  }

  /* ── Mobile Filter & sort: Apply / Remove all → close + top ─ */
  var _dismissMobileFacetsAfterRender = false;

  function closeMobileFacetsDrawer() {
    document.querySelectorAll('.catalog-toolbar-wrap menu-drawer.mobile-facets__wrapper').forEach(function (drawer) {
      var details = drawer.querySelector(':scope > details') || drawer.querySelector('details.mobile-facets__disclosure');
      if (!details) return;

      var summary = details.querySelector('summary');

      // Dawn MenuDrawer.closeMenuDrawer(event) no-ops when event is undefined —
      // pass a synthetic event so it clears menu-opening + body overflow.
      if (typeof drawer.closeMenuDrawer === 'function') {
        drawer.closeMenuDrawer(new Event('catalog-dismiss'), summary || false);
      }

      // Hard reset: Apply / Remove-all must not leave menu-opening behind.
      // (That class alone shows the X on the Filter & sort bar and can keep
      // overflow-hidden-mobile on <body>, blocking the page.)
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
      details.querySelectorAll('details').forEach(function (nested) {
        nested.removeAttribute('open');
        nested.classList.remove('menu-opening');
      });
      details.querySelectorAll('.submenu-open').forEach(function (el) {
        el.classList.remove('submenu-open');
      });
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });

    document.body.classList.remove('overflow-hidden-mobile');
  }

  function clearOrphanedMobileFacetsLock() {
    var locked = document.querySelector(
      '.catalog-toolbar-wrap .mobile-facets__disclosure[open], .catalog-toolbar-wrap .mobile-facets__disclosure.menu-opening'
    );
    if (locked) return;
    document.body.classList.remove('overflow-hidden-mobile');
    document.querySelectorAll('.catalog-toolbar-wrap .mobile-facets__disclosure.menu-opening').forEach(function (el) {
      el.classList.remove('menu-opening');
    });
  }

  function scrollCatalogResultsToTop() {
    var target =
      document.querySelector('.catalog-toolbar-wrap') ||
      document.getElementById('product-grid') ||
      document.querySelector('.collection');
    if (!target) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    var top = target.getBoundingClientRect().top + window.pageYOffset - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function dismissMobileFacets() {
    closeMobileFacetsDrawer();
    scrollCatalogResultsToTop();
    syncMobileFilterFooterVisibility(
      (function () {
        var footerEl = document.querySelector('footer.footer');
        if (!footerEl) return false;
        var rect = footerEl.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      })()
    );
  }

  function initMobileFacetsDismiss() {
    document.querySelectorAll('.catalog-toolbar-wrap .catalog-toolbar__mob-apply').forEach(function (btn) {
      if (btn._catalogDismissBound) return;
      btn._catalogDismissBound = true;
      btn.addEventListener('click', function () {
        dismissMobileFacets();
      });
    });

    document.querySelectorAll('.catalog-toolbar-wrap .mobile-facets__clear').forEach(function (link) {
      if (link._catalogDismissBound) return;
      link._catalogDismissBound = true;
      link.addEventListener('click', function () {
        _dismissMobileFacetsAfterRender = true;
        dismissMobileFacets();
      });
    });
  }

  /* ── Bootstrap ─────────────────────────────────────────────── */
  function init() {
    initShopByToggle();
    initFilterDropdowns();
    initShopByCards();
    initProductSearch();
    // Covers fields Liquid can't mark disabled (the panel's price-facet
    // inputs come from the shared Dawn snippet).
    syncShopByFieldState();
    syncCollectionHero();
    initMobileFilterFooterHide();
    initMobileFacetsDismiss();
    clearOrphanedMobileFacetsLock();
  }

  init();
  preserveState();
})();
